/**
 * Per-org white-label (feature #85, engine `docs/org-subdomains.md`).
 *
 * On `<org>.app.semanticad.ai` the engine answers public `GET /v1/branding` with
 * that org's display name, https logo URL and accent colour. Everywhere else
 * (apex host, unknown host, feature off) it answers 404 and the SPA keeps the
 * SemantiCAD look. Branding is cosmetic: a failed fetch falls back to the
 * defaults with a console warning, and login never waits on it.
 *
 * The payload is still re-validated here before any of it reaches the DOM — the
 * accent goes into a CSS custom property and the logo into an `<img src>`.
 */
import { ENGINE_BASE } from "./config";

export interface Branding {
  /** DNS label of the org host (`acme-metal`); null if the engine sent none. */
  subdomain: string | null;
  displayName: string | null;
  logoUrl: string | null;
  /** `#rrggbb`, lowercase. */
  accentColor: string | null;
}

export const DEFAULT_TITLE = "SemantiCAD";
/** Engine `BRANDING_DISPLAY_NAME_MAX` / `BRANDING_LOGO_URL_MAX`. */
export const BRANDING_NAME_MAX = 80;
export const BRANDING_LOGO_URL_MAX = 2048;

const HEX_RE = /^#[0-9a-f]{6}$/i;
// Control, format (incl. bidi overrides), line and paragraph separators — engine rejects the same.
const FORBIDDEN_NAME_RE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}<>]/u;

/** `#rrggbb` (any case, surrounding space ok) → lowercase; anything else → null. */
export function normalizeAccent(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  return HEX_RE.test(value) ? value.toLowerCase() : null;
}

/** https URL with a host and no credentials, else null. */
export function safeLogoUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value || value.length > BRANDING_LOGO_URL_MAX) return null;
  if (/[\s<>"'`\\]/.test(value)) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || !url.hostname || url.username || url.password) return null;
  return value;
}

export function safeDisplayName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.normalize("NFC").trim();
  if (!value || value.length > BRANDING_NAME_MAX || FORBIDDEN_NAME_RE.test(value)) return null;
  return value;
}

/** Engine JSON → validated branding. Unknown keys are ignored. */
export function parseBranding(body: unknown): Branding {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const sub = typeof b.subdomain === "string" && /^[a-z0-9-]{1,63}$/.test(b.subdomain) ? b.subdomain : null;
  return {
    subdomain: sub,
    displayName: safeDisplayName(b.display_name),
    logoUrl: safeLogoUrl(b.logo_url),
    accentColor: normalizeAccent(b.accent_color),
  };
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2 relative luminance of a `#rrggbb` colour. */
export function relativeLuminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const ACCENT_INK_LIGHT = "#ffffff";
/** `--color-ink` / zinc-900 — the darkest text the design uses. */
export const ACCENT_INK_DARK = "#18181b";

/**
 * Text colour for content on an accent fill: white or the design ink, whichever
 * contrasts more. Mid-tones where neither reaches WCAG AA (4.5:1) fall to pure
 * black — white or black always clears 4.5:1 on an opaque colour.
 */
export function accentInk(accent: string): string {
  const light = contrastRatio(accent, ACCENT_INK_LIGHT);
  const dark = contrastRatio(accent, ACCENT_INK_DARK);
  if (Math.max(light, dark) >= 4.5) return light >= dark ? ACCENT_INK_LIGHT : ACCENT_INK_DARK;
  return light >= contrastRatio(accent, "#000000") ? ACCENT_INK_LIGHT : "#000000";
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * `GET /v1/branding`. Null means "SemantiCAD defaults": 404 quietly (not an org
 * host), anything else with a console warning. Never throws.
 */
export async function fetchBranding(
  opts: { fetchImpl?: FetchLike; base?: string; fresh?: boolean; timeoutMs?: number } = {},
): Promise<Branding | null> {
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  const base = opts.base ?? ENGINE_BASE;
  let res: Response;
  try {
    res = await fetchImpl(`${base}/v1/branding`, {
      method: "GET",
      // Public route: no Bearer / API key. Cookies are host-only anyway.
      credentials: "include",
      headers: { Accept: "application/json" },
      // After an owner saves branding, skip the engine's 60 s public cache.
      cache: opts.fresh ? "no-store" : "default",
      signal:
        typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
          ? AbortSignal.timeout(opts.timeoutMs ?? 8000)
          : undefined,
    });
  } catch (err) {
    console.warn("[branding] fetch failed; using SemantiCAD defaults", err);
    return null;
  }
  if (res.status === 404) return null;
  if (!res.ok) {
    console.warn(`[branding] /v1/branding answered ${res.status}; using SemantiCAD defaults`);
    return null;
  }
  let branding: Branding;
  try {
    branding = parseBranding(await res.json());
  } catch (err) {
    console.warn("[branding] unreadable /v1/branding body; using SemantiCAD defaults", err);
    return null;
  }
  // The engine always names the org host; without it this is not a workspace answer.
  if (!branding.subdomain) {
    console.warn("[branding] /v1/branding answered without a subdomain; using SemantiCAD defaults");
    return null;
  }
  return branding;
}

/**
 * Push branding into the document: `--org-accent` / `--org-accent-ink` on
 * `:root` (tokens live there, not under a shell class) and the tab title.
 * `null` restores the defaults.
 */
export function applyBranding(branding: Branding | null, doc: Document = document): void {
  const root = doc.documentElement;
  const accent = branding?.accentColor ?? null;
  if (accent) {
    root.style.setProperty("--org-accent", accent);
    root.style.setProperty("--org-accent-ink", accentInk(accent));
    root.setAttribute("data-org-accent", "");
  } else {
    root.style.removeProperty("--org-accent");
    root.style.removeProperty("--org-accent-ink");
    root.removeAttribute("data-org-accent");
  }
  doc.title = branding?.displayName || DEFAULT_TITLE;
}

/** Initials for a logo-less org tile. */
export function brandInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return Array.from(parts[0]).slice(0, 2).join("").toUpperCase();
  return (Array.from(parts[0])[0] + Array.from(parts[1])[0]).toUpperCase();
}

/**
 * The apex app URL for an org host (`acme.app.semanticad.ai` → `https://app.semanticad.ai`),
 * or null when the current host does not start with the org's label.
 */
export function apexUrlFor(subdomain: string | null, loc: Pick<Location, "protocol" | "host"> = location): string | null {
  if (!subdomain) return null;
  const prefix = `${subdomain}.`;
  if (!loc.host.startsWith(prefix)) return null;
  const rest = loc.host.slice(prefix.length);
  if (!rest.includes(".")) return null;
  return `${loc.protocol}//${rest}`;
}

export type BrandingField = "display_name" | "logo_url" | "accent_color";
export type BrandingDraft = Record<BrandingField, string>;
export type BrandingFieldErrors = Partial<Record<BrandingField, string>>;

/** Client-side check matching the engine rules; empty means "clear the field". */
export function validateBrandingDraft(draft: BrandingDraft): Partial<Record<BrandingField, true>> {
  const bad: Partial<Record<BrandingField, true>> = {};
  if (draft.display_name.trim() && !safeDisplayName(draft.display_name)) bad.display_name = true;
  if (draft.logo_url.trim() && !safeLogoUrl(draft.logo_url)) bad.logo_url = true;
  if (draft.accent_color.trim() && !normalizeAccent(draft.accent_color)) bad.accent_color = true;
  return bad;
}

/** Draft → PATCH body: trimmed, empty → null (engine clears it), accent lowercased. */
export function brandingPatchFromDraft(draft: BrandingDraft): Record<BrandingField, string | null> {
  const name = draft.display_name.normalize("NFC").trim();
  const logo = draft.logo_url.trim();
  const accent = draft.accent_color.trim();
  return {
    display_name: name || null,
    logo_url: logo || null,
    accent_color: accent ? accent.toLowerCase() : null,
  };
}

/**
 * Which field an engine 422 is about. `org_settings.validate_branding` words
 * every message `branding.<field> …`; anything else is a form-level error.
 */
export function brandingFieldOfError(message: string): BrandingField | null {
  const m = /\bbranding\.(display_name|logo_url|accent_color)\b/.exec(message);
  return m ? (m[1] as BrandingField) : null;
}
